import React, { useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Button, Input, message } from 'antd'
import './styles.less'
import { wheelCores, wheelGeometry } from '../EmotionWheel/data'
import { buildWheel } from '../EmotionWheel/geometry'
import SuperText from '../Elements/SuperText'
import { seededResponses, colorFor, coreFor, coreOrder } from './data'

const { TextArea } = Input

const MAX_PICKS = 3

// Prototype stand-in for the real total the backend will report. Drives the
// "where do you stand?" hook - swap for the live count later.
const TOTAL_ANSWERS = 1240

// A response may be a single-feeling seed object ({emotion, note}) or a
// multi-feeling submission ({feelings:[...]}). Normalise to a feelings array.
const feelingsOf = (r) => r.feelings || [{ emotion: r.emotion, note: r.note }]

const countByCore = (responses) => {
  const map = {}
  coreOrder.forEach((c) => {
    map[c.name] = 0
  })
  responses.forEach((r) =>
    feelingsOf(r).forEach((f) => {
      const core = coreFor(f.emotion)
      map[core] = (map[core] || 0) + 1
    })
  )
  return map
}

// Static, fully-visible wheel. Every wedge is selectable; up to MAX_PICKS can
// be active at once, each shown with a marker in its own colour.
const SelectableWheel = ({
  segments,
  labels,
  centroids,
  selected,
  onToggle,
}) => (
  <div className="es-wheelwrap">
    <svg
      viewBox={`${wheelGeometry.viewBox.join(' ')}`}
      aria-label="Emotion wheel"
    >
      {segments.map((seg) => (
        <path
          key={seg.id}
          d={seg.d}
          fill={seg.fill}
          className={classNames(
            'es-seg',
            selected.includes(seg.emotion) && 'is-active'
          )}
          onClick={() => onToggle(seg.emotion)}
        />
      ))}
      {labels.map((label) => (
        <text
          key={label.text}
          x={label.x}
          y={label.y}
          fontSize={label.size}
          textAnchor="middle"
          dominantBaseline="middle"
          className={classNames(
            'es-lab',
            label.cls,
            selected.includes(label.text) && 'is-active'
          )}
          transform={`rotate(${label.rotation} ${label.x} ${label.y})`}
        >
          {label.text}
        </text>
      ))}
      {selected.map(
        (em) =>
          centroids[em] && (
            <circle
              key={em}
              className="es-marker"
              cx={centroids[em].x}
              cy={centroids[em].y}
              r={15}
              fill={colorFor(em)}
            />
          )
      )}
    </svg>
  </div>
)

const PickCard = ({ pick, onNote, onRemove }) => (
  <div className="es-slot is-filled" style={{ '--pc': colorFor(pick.emotion) }}>
    <div className="es-slot-head">
      <span className="es-slot-name">{pick.emotion}</span>
      <button
        type="button"
        className="es-slot-remove"
        onClick={() => onRemove(pick.emotion)}
        aria-label={`Remove ${pick.emotion}`}
      >
        &times;
      </button>
    </div>
    <TextArea
      value={pick.note}
      onChange={(e) => onNote(pick.emotion, e.target.value)}
      rows={3}
      maxLength={200}
      placeholder={`Why does AI make you feel ${pick.emotion.toLowerCase()}?`}
    />
  </div>
)

const EmptySlot = ({ index }) => (
  <div className="es-slot is-empty">
    <span className="es-slot-num">{index}</span>
    <span className="es-slot-hint">Tap a feeling on the wheel</span>
  </div>
)

const Distribution = ({ responses }) => {
  const counts = useMemo(() => countByCore(responses), [responses])
  const max = Math.max(1, ...Object.values(counts))

  return (
    <div className="es-dist">
      {coreOrder.map((c) => (
        <div className="es-dist-row" key={c.name}>
          <span className="es-dist-label">{c.name}</span>
          <span className="es-dist-track">
            <span
              className="es-dist-bar"
              style={{
                width: `${(counts[c.name] / max) * 100}%`,
                background: c.color,
              }}
            />
          </span>
          <span className="es-dist-count">{counts[c.name]}</span>
        </div>
      ))}
    </div>
  )
}

// The "where do you stand?" hook: a blurred, locked preview of the real
// distribution shown before the visitor answers, to make them curious to
// reveal it.
const RevealTeaser = ({ responses }) => {
  const counts = countByCore(responses)
  const max = Math.max(1, ...Object.values(counts))
  return (
    <div className="es-teaser">
      <div className="es-teaser-bars" aria-hidden="true">
        {coreOrder.map((c) => (
          <span className="es-teaser-track" key={c.name}>
            <span
              className="es-teaser-bar"
              style={{
                width: `${(counts[c.name] / max) * 100}%`,
                background: c.color,
              }}
            />
          </span>
        ))}
      </div>
      <div className="es-teaser-overlay">
        <span className="es-teaser-lock" role="img" aria-label="Locked">
          🔒
        </span>
        <p>
          See where you stand.
          <br />
          <strong>{TOTAL_ANSWERS.toLocaleString()}</strong> people already have.
        </p>
      </div>
    </div>
  )
}

const ResponseCard = ({ response }) => {
  const feelings = feelingsOf(response)
  return (
    <div className={classNames('es-response', response.mine && 'is-mine')}>
      <div className="es-response-tags">
        {feelings.map((f) => (
          <span
            className="es-response-tag"
            key={f.emotion}
            style={{ background: colorFor(f.emotion) }}
          >
            {f.emotion}
          </span>
        ))}
        {response.mine && <span className="es-response-you">your answer</span>}
      </div>
      {feelings.map(
        (f, i) =>
          f.note && (
            <p className="es-response-note" key={i}>
              {f.note}
            </p>
          )
      )}
    </div>
  )
}

export const EmotionSurvey = () => {
  const { segments, labels, centroids } = useMemo(
    () => buildWheel({ cores: wheelCores, geometry: wheelGeometry }),
    []
  )

  const [picks, setPicks] = useState([]) // [{ emotion, note }]
  const [submitted, setSubmitted] = useState(false)
  const [responses, setResponses] = useState(seededResponses)

  const resultsRef = useRef(null)

  const scrollToResults = () => {
    if (typeof window === 'undefined' || !resultsRef.current) return
    window.requestAnimationFrame(() =>
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    )
  }

  const toggleEmotion = (emotion) => {
    setPicks((prev) => {
      if (prev.some((p) => p.emotion === emotion)) {
        return prev.filter((p) => p.emotion !== emotion)
      }
      if (prev.length >= MAX_PICKS) {
        message.info(`You can pick up to ${MAX_PICKS} feelings.`)
        return prev
      }
      return [...prev, { emotion, note: '' }]
    })
  }

  const setNote = (emotion, note) =>
    setPicks((prev) =>
      prev.map((p) => (p.emotion === emotion ? { ...p, note } : p))
    )

  const removeEmotion = (emotion) =>
    setPicks((prev) => prev.filter((p) => p.emotion !== emotion))

  const handleSubmit = () => {
    if (!picks.length) return
    const entry = {
      mine: true,
      feelings: picks.map((p) => ({ emotion: p.emotion, note: p.note.trim() })),
    }
    setResponses((prev) => [entry, ...prev])
    setSubmitted(true)
    message.success('Thank you for sharing.')
    scrollToResults()
  }

  const handleAddAnother = () => {
    setSubmitted(false)
    setPicks([])
  }

  const selected = picks.map((p) => p.emotion)

  const shareUrl =
    typeof window !== 'undefined'
      ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
          window.location.href
        )}`
      : '#'

  return (
    <div className="emotion-survey">
      {!submitted && (
        <div className="es-layout">
          <aside className="es-side">
            <SuperText text="AI survey 2026" />
            <h1 className="es-title">How does AI really make you feel?</h1>
            <p className="es-sub">
              Rarely just one thing. Pick up to three feelings AI stirs up in
              you, add a few words on each - then see where you stand next to{' '}
              <strong>{TOTAL_ANSWERS.toLocaleString()}</strong> others.
              Anonymous, no logins.
            </p>

            <div className="es-instructions">
              <span className="es-counter">
                {picks.length}
                <span className="es-counter-max"> / {MAX_PICKS}</span>
              </span>
              <span className="es-instructions-text">
                {picks.length
                  ? 'chosen. Tap the wheel to add or remove.'
                  : 'chosen. Tap a feeling on the wheel to start.'}
              </span>
            </div>

            <div className="es-slots">
              {Array.from({ length: MAX_PICKS }).map((_, i) =>
                picks[i] ? (
                  <PickCard
                    key={picks[i].emotion}
                    pick={picks[i]}
                    onNote={setNote}
                    onRemove={removeEmotion}
                  />
                ) : (
                  <EmptySlot key={`empty-${i}`} index={i + 1} />
                )
              )}
            </div>

            <div className="es-submit">
              <Button
                type="primary"
                size="large"
                block
                disabled={!picks.length}
                onClick={handleSubmit}
              >
                Share &amp; see where you stand
              </Button>
            </div>

            <RevealTeaser responses={responses} />
          </aside>

          <main className="es-wheel-col">
            <SelectableWheel
              segments={segments}
              labels={labels}
              centroids={centroids}
              selected={selected}
              onToggle={toggleEmotion}
            />
          </main>
        </div>
      )}

      {submitted && (
        <div className="es-results" ref={resultsRef}>
          <h2 className="es-results-title">You&apos;re not alone.</h2>
          <p className="es-results-sub">
            Here&apos;s how {responses.length} people really feel about AI right
            now.
          </p>

          <Distribution responses={responses} />

          <div className="es-feed">
            {responses.map((r, i) => (
              <ResponseCard
                key={r.mine ? `mine-${i}` : `${feelingsOf(r)[0].emotion}-${i}`}
                response={r}
              />
            ))}
          </div>

          <div className="es-share">
            <p>Know someone who&apos;d answer honestly? Pass it on.</p>
            <div className="es-share-actions">
              <Button
                type="primary"
                size="large"
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Share on LinkedIn
              </Button>
              <Button type="default" size="large" onClick={handleAddAnother}>
                Answer again
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EmotionSurvey
